import React, { forwardRef, Ref, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Trigger, PopoverTriggerProps } from '@radix-ui/react-popover';

import {
  textColors,
  neutrals,
  utilityColors,
  spacing,
  iconColors,
  backgroundColors,
} from 'ui/theme';
import { Help } from 'ui/icons/action/Help';

/**
 * popover triggers are instantiated as button elements
 * if the asChild prop is not provided and set to true.
 * We need to remove the default HTML button styling we don't want to use
 * */
const NOT_AS_CHILD_STYLE = css`
  border: none;
  background: inherit;
  padding: unset;
`;
export const DefaultTriggerIcon = Help;

/**
 * Styled popover trigger component containing popover base style overrides
 */
const StyledTrigger = styled(Trigger)<PopoverTriggerProps>`
  ${({ asChild }) => !asChild && NOT_AS_CHILD_STYLE}
`;

type InteractiveIconProps = {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  hasFocus: boolean;
  isHover: boolean;
};
export const InteractiveStates = {
  hover: {
    bgColor: backgroundColors.backgroundTertiary,
    icon: neutrals.grey80,
  },
  focus: {
    bgColor: utilityColors.interactive10,
    icon: iconColors.iconInteractive,
  },
  idle: {
    bgColor: 'transparent',
    icon: textColors.textSecondary,
  },
};
const IconContainer = styled.div<{ bgColor: string }>`
  background-color: ${(props) => props.bgColor};
  border-radius: ${spacing.x3};
  display: flex;
`;
const InteractiveIcon: React.FC<React.PropsWithChildren<InteractiveIconProps>> = ({
  Icon,
  hasFocus,
  isHover,
}) => {
  let iconState = InteractiveStates.idle;
  if (hasFocus) {
    iconState = InteractiveStates.focus;
  } else if (isHover) {
    iconState = InteractiveStates.hover;
  }
  return (
    <IconContainer bgColor={iconState.bgColor}>
      <Icon color={iconState.icon} />
    </IconContainer>
  );
};

/**
 * Base popover trigger component used with other popover elements.
 */
export interface CorePopoverTriggerProps extends PopoverTriggerProps {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isOpen?: boolean;
}
export const PopoverTrigger = forwardRef<HTMLButtonElement, CorePopoverTriggerProps>(
  ({ children, Icon, isOpen = false, ...props }, forwardedRef: Ref<HTMLButtonElement>) => {
    const [isHover, setHover] = useState(false);
    let triggerContent = null;
    triggerContent = useMemo(() => {
      return Icon ? <InteractiveIcon Icon={Icon} isHover={isHover} hasFocus={isOpen} /> : null;
    }, [Icon, isHover, isOpen]);

    return (
      <StyledTrigger
        data-test="popover-trigger"
        {...props}
        ref={forwardedRef}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}
      >
        {triggerContent || children}
      </StyledTrigger>
    );
  },
);
